import { useCallback, useEffect, useMemo, useState } from "react";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import baseUrl from "../../api/baseUrl";
import { isPromotionalSmsEnabled } from "../../utils/clinicFeatures";
import SmsSettingsPanel from "../myaccount/sms/SmsSettingsPanel";
import { MetricTile, PanelCard, SectionHeader } from "../myaccount/components/accountUi";

type SmsBalanceData = {
  configured?: boolean;
  totalAvailable?: number;
  tokenCredits?: number;
  lowBalance?: boolean;
  lowBalanceThreshold?: number;
};

type SmsUsageStats = {
  last7Days?: number;
  last30Days?: number;
  lastSentAt?: string | null;
};

type SmsEditorTab = "regular" | "promotional";

function formatSmsBalance(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return "0";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatSmsDate(value: string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

export default function ClinicSmsSettingsPage() {
  const promotionalSmsEnabled = isPromotionalSmsEnabled();
  const [activeTab, setActiveTab] = useState<SmsEditorTab>("regular");
  const [balance, setBalance] = useState<SmsBalanceData | null>(null);
  const [usage, setUsage] = useState<SmsUsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSmsOverview = useCallback((refreshBalance = false) => {
    setLoading(true);
    setError(null);
    return Promise.all([
      baseUrl.get("/client/account/sms-balance", {
        params: { refresh: refreshBalance ? "true" : "false" },
      }),
      baseUrl.get("/client/account/sms-usage-stats"),
    ])
      .then(([balanceRes, statsRes]) => {
        if (balanceRes?.data?.success) {
          setBalance(balanceRes.data.data ?? null);
        } else {
          setBalance(null);
        }

        if (statsRes?.data?.success) {
          setUsage(statsRes.data.data?.usage ?? null);
        } else {
          setUsage(null);
        }

        if (!balanceRes?.data?.success && !statsRes?.data?.success) {
          setError("Could not load SMS details right now.");
        }
      })
      .catch(() => {
        setBalance(null);
        setUsage(null);
        setError("Could not load SMS details right now.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = "SMS";
  }, []);

  useEffect(() => {
    loadSmsOverview();
  }, [loadSmsOverview]);

  const availableTokens = useMemo(
    () => balance?.totalAvailable ?? balance?.tokenCredits ?? 0,
    [balance?.tokenCredits, balance?.totalAvailable]
  );

  return (
    <div className="space-y-4">
      <PanelCard className="overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <SectionHeader
              icon={SmsOutlinedIcon}
              title="SMS"
              subtitle="Manage clinic templates for regular and promotional SMS"
            />
            <button
              type="button"
              onClick={() => loadSmsOverview(true)}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <RefreshIcon
                sx={{ fontSize: 16 }}
                className={loading ? "animate-spin text-primary" : ""}
              />
              Refresh SMS status
            </button>
          </div>
        </div>

        <div className="space-y-4 p-5">
          {error ? (
            <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
              {error}
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-3">
            <MetricTile label="Available SMS tokens" value={formatSmsBalance(availableTokens)} accent />
            <MetricTile label="Sent (30 days)" value={usage?.last30Days ?? 0} />
            <MetricTile label="Sent (7 days)" value={usage?.last7Days ?? 0} />
          </div>

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_260px]">
            <div className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-1 dark:border-slate-700/80 dark:bg-slate-900/40">
              <div className="grid gap-1 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setActiveTab("regular")}
                  className={`rounded-xl px-4 py-3 text-left transition ${
                    activeTab === "regular"
                      ? "bg-white text-primary shadow-sm dark:bg-slate-800 dark:text-primaryLight"
                      : "text-slate-600 hover:bg-white/70 dark:text-slate-300 dark:hover:bg-slate-800/70"
                  }`}
                >
                  <p className="text-sm font-semibold">Regular SMS</p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Welcome, appointment, reminder, and invoice templates
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("promotional")}
                  className={`rounded-xl px-4 py-3 text-left transition ${
                    activeTab === "promotional"
                      ? "bg-white text-primary shadow-sm dark:bg-slate-800 dark:text-primaryLight"
                      : "text-slate-600 hover:bg-white/70 dark:text-slate-300 dark:hover:bg-slate-800/70"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold">Promotional SMS</p>
                    {!promotionalSmsEnabled ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                        <LockOutlinedIcon sx={{ fontSize: 12 }} />
                        Locked
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Birthday, follow-up, holiday, oral-health, and promo messages
                  </p>
                </button>
              </div>
            </div>

            <PanelCard className="p-4">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Last SMS sent</p>
              <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-white">
                {formatSmsDate(usage?.lastSentAt)}
              </p>
              {balance?.lowBalance ? (
                <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
                  Low balance
                  {balance.lowBalanceThreshold != null
                    ? ` (below ${formatSmsBalance(balance.lowBalanceThreshold)})`
                    : ""}
                </p>
              ) : null}
            </PanelCard>
          </div>
        </div>
      </PanelCard>

      {activeTab === "regular" ? (
        <SmsSettingsPanel mode="regular" onSaved={() => loadSmsOverview(true)} />
      ) : (
        <SmsSettingsPanel
          mode="promotional"
          promotionalLocked={!promotionalSmsEnabled}
          onSaved={() => loadSmsOverview(true)}
        />
      )}
    </div>
  );
}
